import { isSameDay, isValid, parse } from "date-fns";
import React, { useEffect, useMemo } from "react";
import { useDebounceValue } from "usehooks-ts";

import {
	ButtonStandard,
	DatePicker,
	InputText,
	InputWrapper,
	ModalBase,
	SingleSelectInput,
	Text,
} from "@performyard/py-components";
import type { VideoConferencingProviderEnum } from "@performyard/service-contracts";

import InputTextArea from "../../../../../components/Inputs/InputTextArea";
import { Checkboxes } from "../../../../../components/Inputs/SelectionOptions/Checkboxes";
import { VIDEO_CONFERENCING_PROVIDER_CONFIG } from "../../../../../components/VideoConferencing";

import { useLatestMeetingCarryOverItems } from "../../../../../api/hooks";
import { useCalendarIntegrationStatus, useVideoConferencingEligibility } from "../../hooks";

import { hasMeetingStarted } from "../../../utils/meetingTiming";

import { config } from "../../config";
import type { FormErrors, MeetingFormSchema } from "./meeting-form-schema";
import {
	type MeetingFormValuesWithOptionalDates,
	generateTimeOptions,
	handleEndDateChange,
	handleStartDateChange,
	handleStartTimeChange,
} from "./meeting-form-utils";

export type MeetingFormData = {
	name: string;
	description: string;
	startDateTime: Date;
	endDateTime: Date;
	carryOver: boolean;
	addToCalendar: boolean;
	videoConferencingProvider: VideoConferencingProviderEnum | null;
};

type MeetingFormProps = {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: MeetingFormData) => void;
	formValues: MeetingFormValuesWithOptionalDates;
	onFormChange: (newValues: Partial<MeetingFormSchema>) => void;
	validationErrors: FormErrors;
	submitLabel: string;
	isPending: boolean;
	title: string;
	showCarryOver?: boolean;
	/** Shows the calendar + video conferencing options. Each one self-gates on its own connection
	 *  eligibility, so passing this still hides an integration the user hasn't connected. */
	showMeetingIntegrations?: boolean;
	calendarUpdateNotification?: string;
	workspaceOptions?: { label: string; value: string }[];
};

const MeetingForm = ({
	isOpen,
	onClose,
	onSubmit,
	formValues,
	onFormChange,
	validationErrors,
	submitLabel,
	isPending,
	title,
	showCarryOver = false,
	showMeetingIntegrations = true,
	calendarUpdateNotification,
	workspaceOptions,
}: MeetingFormProps): React.ReactElement => {
	const { isCalendarIntegrationEnabled } = useCalendarIntegrationStatus();
	const { connectedProviders, isLoading: isEligibilityLoading } = useVideoConferencingEligibility();

	const startDateTime = useMemo(() => {
		if (!formValues.startDate || !isValid(formValues.startDate)) {
			return new Date();
		}
		return parse(formValues.startTime, "HH:mm", formValues.startDate);
	}, [formValues.startTime, formValues.startDate]);

	const startTimePassed = hasMeetingStarted(startDateTime);

	// A single connected provider renders a checkbox (only one valid choice); several render a
	// provider select (PY-12435).
	const singleConnectedProvider = connectedProviders.length === 1 ? connectedProviders[0].provider : null;

	const [debouncedStartDate] = useDebounceValue(startDateTime, 100);

	const { data: latestMeetingCarryOverItems, isLoading: isLatestMeetingCarryOverItemsLoading } =
		useLatestMeetingCarryOverItems(
			formValues.workspaceId ?? "",
			debouncedStartDate,
			showCarryOver && !!formValues.workspaceId
		);

	const carryOverItemsCount = latestMeetingCarryOverItems?.count ?? 0;

	useEffect(() => {
		if (carryOverItemsCount === 0 && formValues.carryOver) {
			onFormChange({ carryOver: false });
		}
	}, [carryOverItemsCount, formValues.carryOver, onFormChange]);

	// If the calendar integration becomes unusable (e.g. an expired connection cleared after a
	// failed submit), uncheck "add to calendar" so the user can retry and succeed without it.
	useEffect(() => {
		if (!isCalendarIntegrationEnabled && formValues.addToCalendar) {
			onFormChange({ addToCalendar: false });
		}
	}, [isCalendarIntegrationEnabled, formValues.addToCalendar, onFormChange]);

	// A start time can be edited into the past after a provider was picked; drop the now-hidden selection.
	useEffect(() => {
		if (startTimePassed && formValues.videoConferencingProvider) {
			onFormChange({ videoConferencingProvider: null });
		}
	}, [startTimePassed, formValues.videoConferencingProvider, onFormChange]);

	// A provider can disconnect while the form is open (e.g. in another tab); drop a selection
	// that's no longer offered so a stale submit doesn't 400.
	useEffect(() => {
		if (
			!isEligibilityLoading &&
			formValues.videoConferencingProvider &&
			!connectedProviders.some(({ provider }) => provider === formValues.videoConferencingProvider)
		) {
			onFormChange({ videoConferencingProvider: null });
		}
	}, [isEligibilityLoading, connectedProviders, formValues.videoConferencingProvider, onFormChange]);

	const carryOverItemsLabel = `Carryover ${carryOverItemsCount} unchecked item${carryOverItemsCount === 1 ? "" : "s"} from the previous meeting`;
	const carryOverItemsInputDisabled = isPending || isLatestMeetingCarryOverItemsLoading || carryOverItemsCount === 0;

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
		e.preventDefault();

		// If dates are not set, validation will fail in the parent component
		if (!formValues.startDate || !formValues.endDate) {
			onSubmit({
				name: formValues.name.trim(),
				description: formValues.description?.trim() || "",
				startDateTime: new Date(),
				endDateTime: new Date(),
				carryOver: formValues.carryOver,
				addToCalendar: formValues.addToCalendar,
				videoConferencingProvider: formValues.videoConferencingProvider,
			});
			return;
		}

		const startDT = parse(formValues.startTime, "HH:mm", formValues.startDate);
		const endDT = parse(formValues.endTime, "HH:mm", formValues.endDate);

		const data = {
			name: formValues.name.trim(),
			description: formValues.description?.trim() || "",
			startDateTime: startDT,
			endDateTime: endDT,
			carryOver: formValues.carryOver,
			addToCalendar: formValues.addToCalendar,
			videoConferencingProvider: formValues.videoConferencingProvider,
		};

		onSubmit(data);
	};

	const timeOptions = useMemo(() => generateTimeOptions(formValues.startDate ?? new Date()), [formValues.startDate]);

	const availableStartTimeOptions = timeOptions;

	const availableEndTimeOptions = useMemo(() => {
		const endTimeOptions = generateTimeOptions(formValues.endDate ?? new Date());

		// Only filter time options if both dates are valid and on the same day
		if (
			formValues.startDate &&
			formValues.endDate &&
			isValid(formValues.startDate) &&
			isValid(formValues.endDate) &&
			isSameDay(formValues.startDate, formValues.endDate)
		) {
			return endTimeOptions.filter((option) => option.value > formValues.startTime);
		}
		return endTimeOptions;
	}, [formValues.startDate, formValues.endDate, formValues.startTime]);

	return (
		<ModalBase isOpen={isOpen} onClose={onClose} dangerouslySetClassName="w-[466px]">
			<div className="px-6 pb-4 text-center">
				<Text size="sub-heading" weight="semi-bold">
					{title}
				</Text>
				{calendarUpdateNotification && (
					<div className="mt-4">
						<Text size="paragraph-3" weight="regular">
							{calendarUpdateNotification}
						</Text>
					</div>
				)}
			</div>

			<form id="meeting-form" data-testid="meeting-form" onSubmit={handleSubmit} className="px-6 pb-6">
				{workspaceOptions && (
					<>
						<InputWrapper
							id="workspace-select"
							label="Meeting Workspace"
							error={validationErrors.workspaceId}
						>
							<SingleSelectInput<string>
								selectionVariant="combobox"
								options={workspaceOptions}
								fetchOptions={async (searchTerm) =>
									workspaceOptions.filter((o) =>
										o.label.toLowerCase().includes(searchTerm.toLowerCase())
									)
								}
								value={formValues.workspaceId}
								onChange={(value) => onFormChange({ workspaceId: value ?? undefined })}
								placeholder="Make a selection"
								disabled={isPending}
								data-testid="workspace-select"
							/>
						</InputWrapper>
						<div className="h-4" />
					</>
				)}
				<InputWrapper
					id="meeting-name-input"
					label="Meeting Name"
					error={
						validationErrors.name
							? validationErrors.name
							: formValues.name.length > 280
								? "Name cannot exceed 280 characters"
								: undefined
					}
				>
					<InputText
						placeholder="Meeting name here"
						value={formValues.name}
						onChange={(val) => onFormChange({ name: val })}
						disabled={isPending}
						clearable={true}
						maxLength={280}
						data-testid="meeting-name-input"
					/>
				</InputWrapper>
				<div className="h-4" />
				<div className="flex flex-row gap-4">
					<InputWrapper id="start-date-input" label="Start Date" error={validationErrors.startDate}>
						<div style={{ width: config.layout.datePickerWidth }}>
							<DatePicker
								allowEmpty
								value={formValues.startDate ?? null}
								onChange={(val) => handleStartDateChange(val, formValues, onFormChange)}
								disabled={isPending}
								clearable={true}
							/>
						</div>
					</InputWrapper>
					<InputWrapper id="end-date-input" label="End Date" error={validationErrors.endDate}>
						<div style={{ width: config.layout.datePickerWidth }}>
							<DatePicker
								allowEmpty
								value={formValues.endDate ?? null}
								onChange={(val) => handleEndDateChange(val, formValues, onFormChange)}
								disabled={isPending}
								clearable={true}
							/>
						</div>
					</InputWrapper>
				</div>
				<div className="h-4" />
				<div className="grid grid-flow-col grid-cols-2 gap-4">
					<div className="col-span-1" style={{ width: config.layout.datePickerWidth }}>
						<InputWrapper id="start-time-input" label="Start Time" error={validationErrors.startTime}>
							<SingleSelectInput<string>
								options={availableStartTimeOptions}
								value={formValues.startTime}
								onChange={(option) =>
									handleStartTimeChange({ value: option ?? "" }, formValues, onFormChange)
								}
								placeholder="Select a start time"
								disabled={isPending}
								data-testid="start-time-select"
							/>
						</InputWrapper>
					</div>
					<div className="col-span-1" style={{ width: config.layout.datePickerWidth }}>
						<InputWrapper id="end-time-input" label="End Time" error={validationErrors.endTime}>
							<SingleSelectInput<string>
								options={availableEndTimeOptions}
								value={formValues.endTime}
								onChange={(option) => {
									onFormChange({ endTime: option ?? "" });
								}}
								placeholder="Select an end time"
								disabled={isPending}
								data-testid="end-time-select"
							/>
						</InputWrapper>
					</div>
				</div>
				<div className="h-4" />
				<InputTextArea
					value={formValues.description || ""}
					onChange={(val) => onFormChange({ description: val })}
					placeholder="Your meeting description and link"
					minRows={6}
					maxRows={6}
					disabled={isPending}
					label="Description"
					labelSize="paragraph-3"
					errorMessage={
						validationErrors.description
							? validationErrors.description
							: (formValues.description?.length || 0) > config.meetingDescriptionMaxLength
								? `Descriptions cannot exceed ${config.meetingDescriptionMaxLength} characters`
								: ""
					}
					data-testid="meeting-description-input"
				/>
				{showCarryOver && (
					<>
						<div className="h-4" />
						<Checkboxes
							options={[{ value: "carryOver", label: carryOverItemsLabel }]}
							value={formValues.carryOver ? [{ value: "carryOver", label: carryOverItemsLabel }] : []}
							disabled={carryOverItemsInputDisabled ? ["carryOver"] : []}
							onChange={() => onFormChange({ carryOver: !formValues.carryOver })}
						/>
					</>
				)}
				{showMeetingIntegrations && isCalendarIntegrationEnabled && (
					<>
						<div className="h-4" />
						<Checkboxes
							options={[{ value: "addToCalendar", label: "Add Meeting to My Calendar" }]}
							value={
								formValues.addToCalendar
									? [{ value: "addToCalendar", label: "Add Meeting to My Calendar" }]
									: []
							}
							disabled={isPending ? ["addToCalendar"] : []}
							onChange={() => onFormChange({ addToCalendar: !formValues.addToCalendar })}
						/>
					</>
				)}
				{showMeetingIntegrations && !startTimePassed && singleConnectedProvider && (
					<>
						<div className="h-4" />
						<Checkboxes
							options={[{ value: singleConnectedProvider, label: "Add Video Conferencing" }]}
							value={
								formValues.videoConferencingProvider === singleConnectedProvider
									? [{ value: singleConnectedProvider, label: "Add Video Conferencing" }]
									: []
							}
							disabled={isPending ? [singleConnectedProvider] : []}
							onChange={() =>
								onFormChange({
									videoConferencingProvider:
										formValues.videoConferencingProvider === singleConnectedProvider
											? null
											: singleConnectedProvider,
								})
							}
						/>
					</>
				)}
				{showMeetingIntegrations && !startTimePassed && connectedProviders.length > 1 && (
					<>
						<div className="h-4" />
						<InputWrapper id="video-conferencing-select" label="Video Conferencing">
							<SingleSelectInput<VideoConferencingProviderEnum>
								options={connectedProviders.map(({ provider }) => ({
									value: provider,
									label: VIDEO_CONFERENCING_PROVIDER_CONFIG[provider].shortName,
								}))}
								value={formValues.videoConferencingProvider ?? undefined}
								onChange={(value) => onFormChange({ videoConferencingProvider: value ?? null })}
								placeholder="Make a selection"
								isClearable
								disabled={isPending}
								data-testid="video-conferencing-select"
							/>
						</InputWrapper>
					</>
				)}
			</form>

			<div className="flex justify-center gap-2 px-6 pb-6">
				<div key="cancelButton" className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="large"
						onClick={onClose}
						disabled={isPending}
					>
						Cancel
					</ButtonStandard>
				</div>
				<div key="submitButton" className="w-fit min-w-[90px]">
					<ButtonStandard
						size="large"
						color="primary"
						type="submit"
						form="meeting-form"
						disabled={isPending || isLatestMeetingCarryOverItemsLoading}
						data-testid="meeting-form-submit-button"
					>
						{submitLabel}
					</ButtonStandard>
				</div>
			</div>
		</ModalBase>
	);
};

export default MeetingForm;
